import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, Loader2, BookOpen,
  CheckCircle2, AlertCircle, Clock, FileType, Link2, Globe, Instagram, Youtube, ExternalLink,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/markdown",
];
const ACCEPTED_EXTS = ".pdf,.docx,.txt,.md";
const MAX_SIZE_MB = 10;
const IMPORTED_LINK_TYPES = new Set(["website", "youtube", "instagram"]);

const SOURCE_LABELS: Record<string, string> = {
  website: "Site",
  youtube: "YouTube",
  instagram: "Instagram conectado",
};

function StatusBadge({ status }: { status: string }) {
  if (status === "ready")
    return <Badge className="bg-green-100 text-green-700 border-green-200 gap-1"><CheckCircle2 className="w-3 h-3" />Pronto</Badge>;
  if (status === "processing")
    return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 gap-1"><Clock className="w-3 h-3" />Processando</Badge>;
  return <Badge className="bg-red-100 text-red-700 border-red-200 gap-1"><AlertCircle className="w-3 h-3" />Erro</Badge>;
}

function FileTypeIcon({ type }: { type: string }) {
  const colors: Record<string, string> = {
    pdf: "text-red-500",
    docx: "text-blue-500",
    txt: "text-gray-500",
    md: "text-purple-500",
    website: "text-sky-600",
    youtube: "text-red-600",
    instagram: "text-pink-600",
  };
  return <FileType className={`w-5 h-5 ${colors[type] ?? "text-gray-400"}`} />;
}

export default function AdminKnowledge() {
  const utils = trpc.useUtils();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const { data: docs = [], isLoading, refetch } = trpc.knowledge.list.useQuery(undefined, {
    refetchInterval: (query) => {
      // Polling enquanto houver documentos em processamento
      const data = query.state.data as any[];
      return data?.some((d: any) => d.status === "processing") ? 3000 : false;
    },
  });

  const deleteMutation = trpc.knowledge.delete.useMutation({
    onSuccess: () => {
      toast.success("Documento removido com sucesso.");
      utils.knowledge.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const uploadMutation = trpc.knowledge.upload.useMutation({
    onSuccess: (data) => {
      toast.success(`"${data.name}" enviado! Extraindo texto...`);
      utils.knowledge.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const importLinkMutation = trpc.knowledge.importLink.useMutation({
    onSuccess: (data) => {
      toast.success(`Fonte ${SOURCE_LABELS[data.sourceType] ?? "externa"} adicionada à Base de Conhecimento.`);
      setLinkUrl("");
      utils.knowledge.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        // Validar tipo
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
        if (!["pdf", "docx", "txt", "md"].includes(ext)) {
          toast.error(`"${file.name}": formato não suportado. Use PDF, DOCX, TXT ou MD.`);
          continue;
        }
        // Validar tamanho
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          toast.error(`"${file.name}": arquivo muito grande (máximo ${MAX_SIZE_MB}MB).`);
          continue;
        }
        // Converter para base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remover prefixo data:...;base64,
            resolve(result.split(",")[1] ?? result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        await uploadMutation.mutateAsync({
          fileBase64: base64,
          name: file.name,
          fileType: file.name.split(".").pop()?.toLowerCase() ?? "txt",
          mimeType: file.type || "application/octet-stream",
        });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }

  const readyCount = docs.filter((d: any) => d.status === "ready").length;
  const processingCount = docs.filter((d: any) => d.status === "processing").length;

  return (
    <div className="space-y-6 p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <BookOpen className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Base de Conhecimento</h1>
          <p className="text-sm text-muted-foreground">
            Adicione documentos e fontes externas para que o agente de IA responda melhor as dúvidas dos clientes.
          </p>
        </div>
      </div>

      {/* Stats */}
      {docs.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-primary">{docs.length}</div>
            <div className="text-xs text-muted-foreground">Fontes</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{readyCount}</div>
            <div className="text-xs text-muted-foreground">Prontos</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{processingCount}</div>
            <div className="text-xs text-muted-foreground">Processando</div>
          </Card>
        </div>
      )}

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Adicionar Documento</CardTitle>
          <CardDescription>
            Formatos aceitos: PDF, DOCX, TXT, MD — até {MAX_SIZE_MB}MB por arquivo.
            Você pode enviar múltiplos arquivos de uma vez.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
              dragOver
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="text-sm text-muted-foreground">Enviando e extraindo texto...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-muted-foreground" />
                <p className="text-sm font-medium">Clique ou arraste arquivos aqui</p>
                <p className="text-xs text-muted-foreground">PDF, DOCX, TXT, MD — máximo {MAX_SIZE_MB}MB</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTS}
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <div className="mt-3 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              Selecionar arquivos
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Importação controlada de fontes por link */}
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="w-4 h-4 text-primary" />
            Adicionar fonte por link
          </CardTitle>
          <CardDescription>
            Cole um site público, vídeo do YouTube ou o perfil profissional do Instagram já conectado a este salão.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <form
            className="flex flex-col sm:flex-row gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (linkUrl.trim()) importLinkMutation.mutate({ url: linkUrl.trim() });
            }}
          >
            <Input
              type="url"
              inputMode="url"
              autoComplete="url"
              aria-label="Link para importar na Base de Conhecimento"
              placeholder="https://seusite.com.br, YouTube ou Instagram conectado"
              value={linkUrl}
              onChange={(event) => setLinkUrl(event.target.value)}
              disabled={importLinkMutation.isPending}
            />
            <Button type="submit" disabled={!linkUrl.trim() || importLinkMutation.isPending} className="shrink-0">
              {importLinkMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Importar link
            </Button>
          </form>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-muted-foreground">
            <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-2"><Globe className="w-4 h-4 text-sky-600 shrink-0" />Site público</div>
            <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-2"><Youtube className="w-4 h-4 text-red-600 shrink-0" />Vídeo do YouTube</div>
            <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-2"><Instagram className="w-4 h-4 text-pink-600 shrink-0" />Instagram conectado</div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            O sistema salva uma cópia do conteúdo permitido neste momento. Ele não acompanha mudanças do link automaticamente. Para atualizar, importe o link novamente. No YouTube, inclua uma transcrição ou resumo se precisar que o agente conheça detalhes falados no vídeo.
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            No domínio próprio verificado da sua loja, a importação usa o catálogo ativo desta unidade — incluindo produtos, marcas e preços registrados agora — para não depender do carregamento da página no navegador.
          </p>
        </CardContent>
      </Card>

      {/* Document List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fontes carregadas</CardTitle>
          <CardDescription>
            Fontes com status "Pronto" já estão sendo usadas pelo agente nas respostas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">Nenhuma fonte ainda</p>
              <p className="text-xs mt-1">
                Faça upload de documentos ou importe o site, YouTube e Instagram conectado do salão para enriquecer as respostas do agente.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map((doc: any) => (
                (() => {
                  const isImportedLink = IMPORTED_LINK_TYPES.has(doc.fileType);
                  return (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                >
                  <FileTypeIcon type={doc.fileType} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground uppercase">{SOURCE_LABELS[doc.fileType] ?? doc.fileType}</span>
                      {doc.extractedText && (
                        <span className="text-xs text-muted-foreground">
                          · {Math.round(doc.extractedText.length / 1000)}k caracteres
                        </span>
                      )}
                      {doc.errorMessage && (
                        <span className="text-xs text-red-500 truncate max-w-[200px]" title={doc.errorMessage}>
                          · {doc.errorMessage}
                        </span>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={doc.status} />
                  {isImportedLink && doc.fileUrl && (
                    <Button asChild variant="ghost" size="sm" className="text-muted-foreground shrink-0">
                      <a href={doc.fileUrl} target="_blank" rel="noreferrer noopener" aria-label={`Abrir ${SOURCE_LABELS[doc.fileType] ?? "fonte"}`}>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive shrink-0"
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remover documento?</AlertDialogTitle>
                        <AlertDialogDescription>
                          O documento <strong>"{doc.name}"</strong> será removido da base de conhecimento.
                          O agente não terá mais acesso a essas informações.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive hover:bg-destructive/90"
                          onClick={() => deleteMutation.mutate({ id: doc.id })}
                        >
                          Remover
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                  );
                })()
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-4 pb-4">
          <p className="text-sm font-semibold text-primary mb-2">💡 Dicas para melhores resultados</p>
          <ul className="text-xs text-muted-foreground space-y-1.5">
            <li>• <strong>Tabela de preços:</strong> inclua nome do serviço, preço e duração</li>
            <li>• <strong>Políticas:</strong> cancelamento, reagendamento, formas de pagamento</li>
            <li>• <strong>Promoções:</strong> pacotes especiais, combos e condições</li>
            <li>• <strong>Cuidados pós-procedimento:</strong> orientações para bronzeamento, coloração, etc.</li>
            <li>• <strong>FAQ:</strong> perguntas frequentes dos clientes com respostas detalhadas</li>
            <li>• Prefira arquivos TXT ou MD — são processados mais rapidamente que PDFs</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
